import isFunction from 'lodash/isFunction';
import includes from 'lodash/includes';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {getDocument, getWindow} from 'ssr-window';
import IconMenu from '@spothero/icons/menu';
import {Button, Icon, Box, Text} from '@spothero/ui';
import BrandUtils from '@spothero/utils/brand';
import DOMUtils from '@spothero/utils/dom';
import {withAuth0} from '@auth0/auth0-react';
import NavigationCollapsed from './mobile';
import NavigationExpanded from './desktop';
import {compose} from 'redux';
import {connect} from 'react-redux';
import truncate from 'lodash/truncate';
import {CompanyLogo} from './icons';

const document = getDocument();
const window = getWindow();

export class Navigation extends Component {
    static propTypes = {
        user: PropTypes.object,
        isMonolith: PropTypes.bool,
        isProduction: PropTypes.bool,
        expandedData: PropTypes.array,
        collapsedData: PropTypes.array,
        onItemSelected: PropTypes.func,
        headerTitle: PropTypes.string,
        auth0: PropTypes.shape({
            logout: PropTypes.func,
            isAuthenticated: PropTypes.bool,
        }).isRequired,
        searchComponentIsOpen: PropTypes.bool,
        isFullWidth: PropTypes.bool,
    };
    static defaultProps = {
        collapsedData: [],
        expandedData: [],
        headerTitle: '',
        isFullWidth: false,
    };

    constructor(props) {
        super(props);

        this._HTML = DOMUtils.el('html');
        this._viewportLarge = BrandUtils.getBreakpoint('lg');

        this.state = {
            isOpen: false,
        };
    }

    componentDidMount() {
        window.addEventListener('resize', this._onWindowResize);

        this._onWindowResize();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this._onWindowResize);
    }

    _onWindowResize = () => {
        const windowWidth = window.innerWidth || document.body.clientWidth;

        if (windowWidth >= this._viewportLarge && this.state.isOpen) {
            this._onCloseClick();
        }
    };

    _onOpenClick = () => {
        this.setState({
            isOpen: true,
        });

        DOMUtils.addClass(this._HTML, 'nav-menu-open');
    };

    _onCloseClick = cb => {
        this.setState({
            isOpen: false,
        });

        setTimeout(() => {
            DOMUtils.removeClass(this._HTML, 'nav-menu-open');

            if (isFunction(cb)) {
                cb();
            }
        }, 500);
    };

    _onItemSelected = evt => {
        const {onItemSelected} = this.props;
        const isExternalLink = includes(
            DOMUtils.attr(evt.target, 'href'),
            'http'
        );

        if (onItemSelected && !isExternalLink) {
            onItemSelected();
        }
    };

    render() {
        const {
            isProduction,
            isMonolith,
            user,
            collapsedData,
            expandedData,
            headerTitle,
            searchComponentIsOpen,
            isFullWidth,
        } = this.props;
        const {isOpen} = this.state;

        return (
            <Box
                height="14"
                position="relative"
                width="100%"
                maxWidth={isFullWidth ? 'none' : {tablet: '82rem'}}
                backgroundColor="white"
                zIndex="layer10"
                margin="0 auto"
            >
                <Box
                    position={{base: 'fixed', tablet: 'relative'}}
                    width="100%"
                    height="14"
                    backgroundColor="white"
                    zIndex={{base: 'layer10', tablet: '0'}}
                >
                    <CompanyLogo />
                    {headerTitle && (
                        <Text
                            data-testid="Header-title"
                            variant="body2"
                            fontWeight="medium"
                            paddingTop={4}
                        >
                            {truncate(headerTitle, {length: 30})}
                        </Text>
                    )}
                    <Button
                        onClick={this._onOpenClick}
                        variant="tertiary"
                        aria-label="Collapse Navigation Toggle"
                        data-testid="Header-menu-mobile"
                        position="absolute"
                        top="50%"
                        transform="translate(0, -50%)"
                        right="4"
                        transition="opacity .2s; visibility .5s"
                        opacity={searchComponentIsOpen ? '0%' : '100%'}
                        visibility={{
                            desktop: 'hidden',
                            base: searchComponentIsOpen ? 'hidden' : 'visible',
                        }}
                        color="black"
                        display={{desktop: 'none'}}
                        sx={{
                            '&:hover': {
                                background: 'inherit',
                                border: 'none',
                                color: 'black',
                            },
                        }}
                    >
                        <Icon
                            as={IconMenu}
                            data-testid="Header-menu-mobile-icon"
                            height="6"
                            width="6"
                            position="relative"
                            verticalAlign="middle"
                        />
                    </Button>
                </Box>
                <Box position="fixed" zIndex="layer10">
                    {isOpen && (
                        <Box
                            data-testid="NavigationShim"
                            position="fixed"
                            left="0"
                            top="0"
                            bottom="0"
                            right="0"
                            margin="auto"
                            height="100%"
                            width="100%"
                            backgroundColor="rgba(33, 33, 33, 0.6)"
                            onClick={this._onCloseClick}
                        />
                    )}
                    <NavigationCollapsed
                        forceAnchors={isMonolith}
                        user={user}
                        isOpen={isOpen}
                        items={collapsedData}
                        onItemSelected={this._onItemSelected}
                        onCloseClick={this._onCloseClick}
                    />
                </Box>
                <NavigationExpanded
                    forceAnchors={isMonolith}
                    isProduction={isProduction}
                    user={user}
                    items={expandedData}
                    onItemSelected={this._onItemSelected}
                />
            </Box>
        );
    }
}

const mapStateToProps = state => {
    const {
        header: {
            data: {headerTitle},
        },
        search: {
            data: {searchComponentIsOpen},
        },
    } = state;

    return {
        headerTitle,
        searchComponentIsOpen,
    };
};

const enhance = compose(withAuth0, connect(mapStateToProps));

export default enhance(Navigation);
